import { GRAHAS, positionsAtDate, type Graha } from '../../astro/ephemeris.ts';
import { AYANAMSA } from '../../astro/ayanamsa.ts';
import {
  RASHI_ENGLISH,
  formatPlacement,
  placementOf,
  rashiIndexOf,
  wholeSignHouse,
} from '../../astro/zodiac.ts';
import { confident, precisionToMinutes, type TimePrecision } from '../../astro/uncertainty.ts';
import { resolveBirthTime, resolveWithOffset } from '../../astro/time.ts';
import { northIndianChart, southIndianChart, type ChartPlacement } from './chart-svg.ts';
import { readBirthDetails, renderTimeProblem, type BirthDetails } from './birth-details.ts';
import { reportToolComplete, reportToolStart } from './analytics.ts';

const ABBR: Record<Graha, string> = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju',
  Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

interface Row {
  graha: Graha;
  /** Undefined when the birth time can't pin the sign down. */
  signIndex?: number;
  candidates?: string[];
  boundaryAt?: Date | null;
  longitude: number;
  retrograde: boolean;
  nakshatra?: string;
  pada?: number;
  house?: number;
}

/**
 * Builds one row per graha, gating each independently.
 *
 * Degrading per-planet rather than all-or-nothing matters: with a vague birth
 * time the Moon may be unresolvable while Saturn is perfectly safe, and
 * refusing the whole chart would throw away true information.
 */
function buildRows(utc: Date, details: BirthDetails, ascendantSign: number | undefined): Row[] {
  const deltaMinutes = precisionToMinutes(details.precision);
  const place = { latitude: details.latitude, longitude: details.longitude };

  return GRAHAS.map((graha) => {
    const signAt = (t: Date) => rashiIndexOf(positionsAtDate(t, place).longitudes[graha]);
    const gate = confident(signAt, utc, deltaMinutes, (v) => v);

    const pos = positionsAtDate(utc, place);
    const longitude = pos.longitudes[graha];
    const row: Row = { graha, longitude, retrograde: pos.retrograde[graha] };

    if (!gate.certain) {
      row.candidates = gate.candidates.map((i) => RASHI_ENGLISH[i]!);
      row.boundaryAt = gate.boundaryAt;
      return row;
    }

    row.signIndex = gate.value;

    // Nakshatra is a finer division, so it needs its own, stricter gate.
    const nakAt = (t: Date) =>
      placementOf(positionsAtDate(t, place).longitudes[graha]).nakshatraIndex;
    const nakGate = confident(nakAt, utc, deltaMinutes, (v) => v);
    if (nakGate.certain) {
      const p = placementOf(longitude);
      row.nakshatra = p.nakshatra;
      const padaGate = confident(
        (t) => placementOf(positionsAtDate(t, place).longitudes[graha]).pada,
        utc, deltaMinutes, (v) => v,
      );
      if (padaGate.certain) row.pada = padaGate.value;
    }

    if (ascendantSign !== undefined) {
      row.house = wholeSignHouse(longitude, ascendantSign * 30 + 15);
    }
    return row;
  });
}

function renderTable(rows: Row[], hasHouses: boolean): HTMLElement {
  const wrap = el('div', 'tool-table-wrap');
  const table = el('table', 'planet-table');

  const thead = el('thead');
  const hr = el('tr');
  for (const h of ['Graha', 'Sign', 'Degree', 'Nakshatra', ...(hasHouses ? ['House'] : [])]) {
    hr.appendChild(el('th', undefined, h));
  }
  thead.appendChild(hr);
  table.appendChild(thead);

  const tbody = el('tbody');
  for (const row of rows) {
    const tr = el('tr', row.signIndex === undefined ? 'is-uncertain' : undefined);
    tr.appendChild(el('td', 'planet-table__name', row.graha));

    if (row.signIndex === undefined) {
      const td = el('td', 'planet-table__unknown');
      td.colSpan = hasHouses ? 4 : 3;
      td.textContent = `Between ${row.candidates?.join(' and ')} — we need a more exact birth time`;
      tr.appendChild(td);
    } else {
      tr.appendChild(el('td', undefined, RASHI_ENGLISH[row.signIndex]!));
      tr.appendChild(
        el('td', undefined, formatPlacement(row.longitude) + (row.retrograde ? ' ℞' : '')),
      );
      tr.appendChild(
        el('td', undefined, row.nakshatra
          ? row.pada ? `${row.nakshatra} (pada ${row.pada})` : row.nakshatra
          : 'Near a boundary'),
      );
      if (hasHouses) tr.appendChild(el('td', undefined, row.house ? String(row.house) : '—'));
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrap.appendChild(table);
  return wrap;
}

function renderChart(rows: Row[], ascendantSign: number, style: 'north' | 'south'): SVGSVGElement {
  const placements: ChartPlacement[] = rows
    .filter((r) => r.signIndex !== undefined)
    .map((r) => ({
      abbr: ABBR[r.graha],
      signIndex: r.signIndex!,
      retrograde: r.retrograde,
    }));
  return style === 'north'
    ? northIndianChart(placements, ascendantSign)
    : southIndianChart(placements, ascendantSign);
}

export function initBirthChartTool(): void {
  const form = document.getElementById('chart-form') as HTMLFormElement | null;
  if (!form) return;
  const output = document.getElementById('chart-output');
  const skeleton = document.getElementById('chart-skeleton');
  if (!output || !skeleton) return;

  let style: 'north' | 'south' = 'north';

  let started = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const details = readBirthDetails(form);
    if (!details) return;
    if (!started) { started = true; reportToolStart('birth-chart'); }

    output.hidden = true;
    output.classList.remove('is-revealed');
    skeleton.hidden = false;

    window.setTimeout(() => {
      skeleton.hidden = true;
      output.replaceChildren();

      // Resolve the wall clock first — this is where gaps, ambiguities and the
      // pre-1906 LMT trap surface, and none of them may be guessed past.
      let utc: Date;
      if (details.precision === 'unknown') {
        // Noon local, purely so slow bodies can still be reported. The gate
        // below is what stops anything time-sensitive being asserted.
        utc = resolveWithOffset({ ...details.wall, hour: 12, minute: 0 }, 330);
      } else {
        const resolved = resolveBirthTime(details.wall, details.timezone, details.longitude);
        if (!resolved.ok) {
          output.appendChild(renderTimeProblem(resolved));
          output.hidden = false;
          requestAnimationFrame(() => output.classList.add('is-revealed'));
          return;
        }
        utc = resolved.utc;
      }

      const place = { latitude: details.latitude, longitude: details.longitude };
      const deltaMinutes = precisionToMinutes(details.precision);

      // The Ascendant moves a degree every four minutes, so it is gated hardest.
      const ascGate = confident(
        (t) => rashiIndexOf(positionsAtDate(t, place).ascendant!),
        utc, deltaMinutes, (v) => v,
      );
      const ascendantSign = ascGate.certain ? ascGate.value : undefined;

      const rows = buildRows(utc, details, ascendantSign);

      // Header
      const head = el('div', 'chart-result__head');
      head.appendChild(el('p', 'tool-result__where',
        `${details.name || 'Chart'} · ${details.placeLabel}`));
      if (ascendantSign !== undefined) {
        head.appendChild(el('p', 'chart-result__lagna',
          `${RASHI_ENGLISH[ascendantSign]} Lagna`));
      } else {
        head.appendChild(el('p', 'chart-result__lagna is-uncertain', 'Lagna not determined'));
      }
      output.appendChild(head);

      if (ascendantSign === undefined) {
        const warn = el('p', 'tool-caveat');
        warn.textContent = ascGate.certain
          ? ''
          : `The Ascendant changes sign roughly every two hours, and with the birth time you gave it could be ${
              (ascGate as { candidates: number[] }).candidates.map((i) => RASHI_ENGLISH[i]).join(' or ')
            }. Houses are left out rather than guessed. A birth certificate time resolves this.`;
        output.appendChild(warn);
      } else {
        // Chart, with a style toggle.
        const toggle = el('div', 'chart-toggle');
        (['north', 'south'] as const).forEach((s) => {
          const btn = el('button', `chart-toggle__btn${s === style ? ' is-active' : ''}`,
            s === 'north' ? 'North Indian' : 'South Indian');
          btn.type = 'button';
          btn.addEventListener('click', () => {
            style = s;
            const holder = output.querySelector('.chart-holder');
            if (holder) holder.replaceChildren(renderChart(rows, ascendantSign, s));
            output.querySelectorAll('.chart-toggle__btn').forEach((b, i) => {
              b.classList.toggle('is-active', (i === 0) === (s === 'north'));
            });
          });
          toggle.appendChild(btn);
        });
        output.appendChild(toggle);

        const holder = el('div', 'chart-holder');
        holder.appendChild(renderChart(rows, ascendantSign, style));
        output.appendChild(holder);
      }

      output.appendChild(renderTable(rows, ascendantSign !== undefined));

      // Method disclosure — the conventions that decide every value above.
      const method = el('details', 'tool-method');
      method.appendChild(el('summary', undefined, 'How this chart is calculated'));
      method.appendChild(el('p', undefined,
        `Sidereal positions using the ${AYANAMSA.label} ayanamsa. Geocentric and apparent, ` +
        `matching standard Indian panchanga practice. Rahu is the mean lunar node, with Ketu ` +
        `exactly opposite. Houses are whole-sign from the Lagna. Anything the stated birth-time ` +
        `precision cannot settle is left blank rather than guessed.`));
      output.appendChild(method);

      output.hidden = false;
      requestAnimationFrame(() => output.classList.add('is-revealed'));

      /* A chart is rendered. The renderTimeProblem branch above returns
         early, so an unresolvable birth time is not counted as a completion. */
      reportToolComplete('birth-chart');
    }, 500);
  });
}
